import React, { useState } from 'react';

// Globals
import gql from 'graphql-tag';
import { batch, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

// UI
import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Flex, Input, message } from 'antd';

// Locals
import { setEditingChapterId, updateStep as updateStepStore } from '../actions';
import useMutation from '../hooks/useMutation';
import styles from './Components.module.css';
import { handleInputChange, stopPropagation } from './utils';

// Types
import type { GiideRouteParams } from '../types';

const updateStep = gql`
  mutation UpdateStep($input: UpdateStepInput!) {
    updateStep(input: $input) {
      id
      owner
      tenantID
    }
  }
`;

type Props = {
  stepId: string;
  stepTitle: string;
};

const UpdateStep = ({ stepId, stepTitle }: Props) => {
  const { push } = useHistory();
  const { id: giideId } = useParams<GiideRouteParams>();

  if (!giideId) {
    push('/');
  }

  const dispatch = useDispatch();
  const [newTitle, setNewTitle] = React.useState<string>(stepTitle);
  const { callMutation } = useMutation(updateStep);

  const [loading, setLoading] = useState(false);

  const saveEdited = React.useCallback(() => {
    if (!newTitle) {
      message.error('Please input a title for this step.');
      return;
    }

    setLoading(true);

    callMutation({
      id: stepId,
      title: newTitle,
    }).then(() => {
      batch(() => {
        dispatch(
          updateStepStore({
            id: stepId,
            title: newTitle,
            editing: false,
          })
        );

        dispatch(setEditingChapterId(undefined));

        setLoading(false);
      });
    });
  }, [callMutation, newTitle, dispatch, stepId]);

  return (
    <Flex className={styles.updateStep} gap={'small'} justify={'space-between'}>
      <Input
        value={newTitle}
        onClick={e => e.stopPropagation}
        onChange={handleInputChange(setNewTitle)}
        onPressEnter={stopPropagation(saveEdited)}
        placeholder="Type name of step"
        autoFocus
        required
        minLength={1}
        maxLength={60}
      ></Input>

      <Button
        style={{ flexShrink: 0 }}
        type={'text'}
        loading={loading}
        onClick={stopPropagation(saveEdited)}
        icon={<CheckCircleOutlined />}
      ></Button>
    </Flex>
  );
};

export default UpdateStep;
